import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, apiFetch, type ApiError } from '../../api/client';
import { Alert, Field } from '../../components/Field';
import { useLimits } from '../../api/limits';

interface Employee {
  id: string;
  email: string;
  name: string;
  status: string;
  department: { id: string; name: string } | null;
  teams: Array<{ id: string; name: string }>;
  enrolments: number;
  averagePercent: number;
  lastActivityAt: string | null;
}

interface InviteResult {
  invited: number;
  skipped: Array<{ email: string; reason: string }>;
  note: string;
  issued: Array<{ email: string; token: string }>;
}

interface ImportResult {
  rowsRead: number;
  invited: number;
  skippedExisting: number;
  errorCount: number;
  errors: Array<{ line: number; column: string; message: string; value: string }>;
  errorReportCsv: string | null;
}

export function OrgEmployees() {
  const [employees, setEmployees] = useState<Employee[] | null>(null);
  const [term, setTerm] = useState('');
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const load = useCallback(async (q: string) => {
    try {
      const response = await api<{ items: Employee[] }>(
        `/org/employees${q ? `?q=${encodeURIComponent(q)}` : ''}`,
      );
      setEmployees(response.items);
    } catch (caught) {
      setError(caught as ApiError);
    }
  }, []);

  useEffect(() => {
    void load('');
  }, [load]);

  return (
    <>
      <div className="page__head">
        <h1 className="page__title">Company Staff</h1>
        <p className="page__lede">
          Invite people, see how they are getting on, and release the seats of anyone who has left.
        </p>
      </div>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}
      {notice ? <Alert kind="success" title={notice} /> : null}

      <div className="tiles" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
        <Invite
          onDone={(result) => {
            setNotice(
              `${result.invited} invited. ${result.note}`,
            );
            void load(term);
          }}
          onError={setError}
        />
        <ImportCsv
          onDone={(result) => {
            setImportResult(result);
            setNotice(`${result.invited} invited from ${result.rowsRead} rows.`);
            void load(term);
          }}
          onError={setError}
        />
      </div>

      {importResult && importResult.errorCount > 0 ? (
        <div className="card">
          <div className="card__head">
            <span className="card__title">
              {importResult.errorCount} row{importResult.errorCount === 1 ? '' : 's'} could not be
              imported
            </span>
            {importResult.errorReportCsv ? (
              <a
                className="btn btn--outline btn--sm"
                download="import-errors.csv"
                href={`data:text/csv;charset=utf-8,${encodeURIComponent(importResult.errorReportCsv)}`}
              >
                Download the report
              </a>
            ) : null}
          </div>
          <div className="card__body">
            <table className="table">
              <thead>
                <tr>
                  <th>Line</th>
                  <th>Column</th>
                  <th>Value</th>
                  <th>Problem</th>
                </tr>
              </thead>
              <tbody>
                {importResult.errors.slice(0, 25).map((row, index) => (
                  <tr key={`${row.line}-${row.column}-${index}`}>
                    <td>{row.line}</td>
                    <td className="mono">{row.column}</td>
                    <td className="mono">{row.value || '—'}</td>
                    <td>{row.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="card__head">
          <span className="card__title">Directory</span>
          <form
            className="row"
            onSubmit={(event) => {
              event.preventDefault();
              void load(term);
            }}
          >
            <Field
              label=""
              placeholder="Search by name or address"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
            />
            <button className="btn btn--outline btn--sm" type="submit">
              Search
            </button>
          </form>
        </div>

        {employees === null ? (
          <div className="card__body">
            <div className="skel" style={{ height: 120 }} />
          </div>
        ) : employees.length === 0 ? (
          <div className="state">
            <div className="state__art" aria-hidden="true">◎</div>
            <h4>Nobody to show</h4>
            <p>
              Either nobody has joined yet, or your remit does not cover anyone. A manager sees
              only their own team or department.
            </p>
          </div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Department</th>
                <th>Courses</th>
                <th>Average</th>
                <th>Last active</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => (
                <tr key={employee.id}>
                  <td>
                    <strong>{employee.name}</strong>
                    <br />
                    <span className="muted">{employee.email}</span>
                  </td>
                  <td>{employee.department?.name ?? '—'}</td>
                  <td>{employee.enrolments}</td>
                  <td>{employee.averagePercent}%</td>
                  <td className="muted">
                    {employee.lastActivityAt
                      ? new Date(employee.lastActivityAt).toLocaleDateString()
                      : '—'}
                  </td>
                  <td>
                    <span
                      className={`pill pill--${employee.status === 'ACTIVE' ? 'approved' : 'inactive'}`}
                    >
                      {employee.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function Invite({
  onDone,
  onError,
}: {
  onDone: (result: InviteResult) => void;
  onError: (error: ApiError) => void;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const limits = useLimits();

  async function send(event: FormEvent) {
    event.preventDefault();
    const emails = value
      .split(/[\s,;]+/)
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (emails.length === 0) return;

    setBusy(true);
    try {
      onDone(await api<InviteResult>('/org/employees/invite', {
        method: 'POST',
        body: JSON.stringify({ emails }),
      }));
      setValue('');
    } catch (caught) {
      onError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  // Counted as you type, so the ceiling is met before the send rather than
  // after it. Same split the server uses, so the two cannot disagree.
  const entered = value
    .split(/[,\s]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
  const cap = limits?.invitations.maxPerRequest;
  const overCap = cap !== undefined && entered.length > cap;

  return (
    <form className="tile" onSubmit={send}>
      <div className="tile__label">Invite by email</div>
      <textarea
        className="field__input"
        rows={3}
        placeholder="One or more addresses, separated by commas or new lines"
        value={value}
        style={{ margin: '10px 0' }}
        onChange={(event) => setValue(event.target.value)}
        aria-describedby="sx-invite-limits"
      />

      <ul className="tile__rules" id="sx-invite-limits">
        <li>
          <strong>Up to {cap ?? 500} addresses</strong> in one send. Separate them with commas,
          spaces or new lines.
        </li>
        <li>Each address may be up to {limits?.invitations.maxEmailChars ?? 320} characters.</li>
        <li>An invitation reserves no seat. A seat is taken when it is accepted.</li>
      </ul>

      {entered.length > 0 ? (
        <p className={`tile__count${overCap ? ' tile__count--over' : ''}`} role="status">
          {entered.length} address{entered.length === 1 ? '' : 'es'} entered
          {cap !== undefined ? ` of ${cap}` : ''}
          {overCap ? ' — remove some, or send them in two batches.' : ''}
        </p>
      ) : null}

      <button className="btn btn--sm" type="submit" disabled={busy || overCap}>
        {busy ? 'Inviting…' : 'Send invitations'}
      </button>
    </form>
  );
}

function ImportCsv({
  onDone,
  onError,
}: {
  onDone: (result: ImportResult) => void;
  onError: (error: ApiError) => void;
}) {
  const [busy, setBusy] = useState(false);
  const limits = useLimits();

  async function send(event: FormEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];
    if (!file) return;

    setBusy(true);
    try {
      const csv = await file.text();
      // Sent as the file's own text; the response is the validation report.
      const response = await apiFetch('/org/employees/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'text/csv',
          Authorization: `Bearer ${localStorage.getItem('sx.access') ?? ''}`,
        },
        body: csv,
      });
      const payload = await response.json();
      if (!response.ok) throw { ...payload, status: response.status } as ApiError;
      onDone(payload as ImportResult);
    } catch (caught) {
      onError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tile">
      <div className="tile__label">Import a CSV</div>
      <input
        className="field__input"
        type="file"
        accept=".csv,text/csv"
        style={{ margin: '10px 0' }}
        disabled={busy}
        onChange={(event) => void send(event)}
      />
      <ul className="tile__rules">
        <li>
          <strong>Required columns:</strong>{' '}
          {(limits?.csvImport.requiredColumns ?? ['email', 'firstName', 'lastName']).join(', ')}.
        </li>
        <li>
          <strong>Optional:</strong>{' '}
          {(limits?.csvImport.optionalColumns ?? ['department', 'team', 'designation']).join(', ')}.
          A department or team named here must already exist.
        </li>
        <li>
          Up to <strong>{(limits?.csvImport.maxRows ?? 5000).toLocaleString()} rows</strong> and{' '}
          <strong>{limits?.csvImport.maxFileMb ?? 1} MB</strong> per file. A longer file is
          imported up to that point and the report names the line where it stopped, so you can
          split the rest.
        </li>
        <li>Bad rows come back as a report you can fix and re-upload. The good ones still go in.</li>
      </ul>
    </div>
  );
}
